import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const ROWS = [
  {
    id: "work",
    to: "/work",
    testId: "path-explore-work-link",
    eyebrow: "01 — Existing builds",
    headline: "Love something we’ve already built?",
    copy: "Explore our work. Find something you love, and we’ll make it yours—customized for your business, your brand, and your customers.",
    cta: "Explore Our Work",
  },
  {
    id: "custom",
    to: "/start",
    testId: "path-start-custom-link",
    eyebrow: "02 — Custom builds",
    headline: "Want something completely different?",
    copy: "Tell us what you’re imagining. A website, an application, or something that doesn’t quite exist yet. We’ll figure out what it takes to build it.",
    cta: "Start a Custom Project",
  },
];

function useIsMobile() {
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return mobile;
}

export default function Paths() {
  const ref = useRef(null);
  const isMobile = useIsMobile();
  const reduced = useReducedMotion() ?? false;
  // Mobile: no persistent cursor, so the first tap SELECTS a path and holds
  // the same dramatic inversion; the second tap navigates. Desktop keeps its
  // exact hover behavior — this state is never consulted there.
  const [selected, setSelected] = useState(null);

  // Desktop keeps the full ±40px counter-drift. On mobile the rows span the
  // whole viewport width, so the same drift pushes readable copy off the
  // edge — a whisper of movement (±8px) keeps the depth without clipping.
  const range = isMobile ? 8 : 40;
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const driftA = useTransform(scrollYProgress, [0, 1], [range, -range]);
  const driftB = useTransform(scrollYProgress, [0, 1], [-range, range]);

  // THE TRANSFORMATION: the section enters in the SAME dark state as the rest
  // of the page, and the visible environment itself — background, type,
  // borders — cross-fades to the light state while it is on screen. One
  // environment changing state, not a reveal of a pre-styled white section.
  const { scrollYProgress: env } = useScroll({
    target: ref,
    offset: ["start 0.95", "start 0.3"],
  });
  const bg = useTransform(env, [0, 1], ["#050505", "#ffffff"]);
  const fg = useTransform(env, [0, 1], ["#ffffff", "#050505"]);
  const sub = useTransform(env, [0, 1], ["rgba(163,163,163,1)", "rgba(5,5,5,0.6)"]);
  const muted = useTransform(env, [0, 1], ["rgba(255,255,255,0.45)", "rgba(5,5,5,0.5)"]);
  const border = useTransform(env, [0, 1], ["rgba(255,255,255,0.1)", "rgba(5,5,5,0.1)"]);

  const envStyle = reduced
    ? {
        backgroundColor: "#ffffff",
        "--env-fg": "#050505",
        "--env-sub": "rgba(5,5,5,0.6)",
        "--env-muted": "rgba(5,5,5,0.5)",
        "--env-border": "rgba(5,5,5,0.1)",
      }
    : {
        backgroundColor: bg,
        "--env-fg": fg,
        "--env-sub": sub,
        "--env-muted": muted,
        "--env-border": border,
      };

  const drifts = { work: driftA, custom: driftB };

  return (
    <motion.section
      ref={ref}
      data-testid="paths-section"
      style={envStyle}
      className="relative overflow-x-clip pt-24 md:pt-40"
    >
      <p className="mb-14 px-5 text-[10px] font-medium uppercase tracking-[0.5em] text-[color:var(--env-muted)] md:px-16 md:text-xs">
        Two ways in
      </p>

      {ROWS.map((row, ri) => {
        const active = selected === row.id;
        return (
          <div key={row.id}>
            <Link
              to={row.to}
              data-testid={row.testId}
              onClick={(e) => {
                if (isMobile && !active) {
                  e.preventDefault();
                  setSelected(row.id);
                }
              }}
              className={`group block px-5 py-14 transition-colors duration-700 ease-cinematic hover:bg-obsidian md:px-16 md:py-20 ${
                ri === 0 ? "border-t" : "border-y"
              } border-[color:var(--env-border)] ${active ? "bg-obsidian" : ""}`}
            >
              <motion.div
                style={{ x: drifts[row.id] }}
                className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
              >
                <div className="max-w-3xl">
                  <span
                    className={`text-[10px] font-medium uppercase tracking-[0.4em] transition-colors duration-700 md:text-xs ${
                      active
                        ? "text-white/50"
                        : "text-[color:var(--env-muted)] group-hover:text-white/50"
                    }`}
                  >
                    {row.eyebrow}
                  </span>
                  <h3
                    className={`mt-3 font-display text-3xl font-medium leading-[1.05] tracking-[-0.02em] transition-colors duration-700 md:text-6xl ${
                      active
                        ? "text-white"
                        : "text-[color:var(--env-fg)] group-hover:text-white"
                    }`}
                  >
                    {row.headline}
                  </h3>
                  <p
                    className={`mt-5 max-w-xl text-sm leading-relaxed transition-colors duration-700 md:text-base ${
                      active
                        ? "text-white/60"
                        : "text-[color:var(--env-sub)] group-hover:text-white/60"
                    }`}
                  >
                    {row.copy}
                  </p>
                </div>
                <span
                  className={`flex items-center gap-3 whitespace-nowrap font-display text-xl font-medium transition-colors duration-700 md:text-3xl ${
                    active
                      ? "text-white"
                      : "text-[color:var(--env-fg)] group-hover:text-white"
                  }`}
                >
                  {row.cta}
                  <ArrowUpRight className="h-6 w-6 transition-transform duration-500 ease-cinematic group-hover:translate-x-1 group-hover:-translate-y-1 md:h-8 md:w-8" />
                </span>
              </motion.div>
            </Link>
          </div>
        );
      })}

      {/* the light environment dissolves back into the dark experience */}
      <div aria-hidden="true" className="pointer-events-none mt-20 h-40 bg-gradient-to-b from-white to-obsidian md:mt-24 md:h-56" />
    </motion.section>
  );
}
